/**
 * @module UseBoardWideGenerate
 * @purpose Board-wide briefing generation hook with ETA confirm, per-cluster progress, and cancel.
 *   Exposes start, cancel, and progress state for FR7 — no silent multi-hour launch.
 * @depends services/reasoning-ai, services/api, types/reasoning
 * @dependedOnBy pages/case/analysis/reasoning/briefing/BriefingBoardView
 *
 * @rules
 * - Performance: polls each active job until terminal; cancel marks pending jobs cancelled client-side
 * - State: local useState only; no shared context
 * - Errors: 400 provider_not_configured surfaces a clear message; other errors set error string
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../services/api'
import { boardWideGenerate, pollBriefingJob } from '../services/reasoning-ai'
import type {
  BriefingJob,
  BoardWideGenerateRequest,
} from '../types/reasoning'

const POLL_INTERVAL_MS = 5000
const HTTP_BAD_REQUEST = 400

// =============================================================================
// Types
// =============================================================================

export interface BoardJobProgress {
  job: BriefingJob
  cancelled: boolean
}

export interface UseBoardWideGenerateResult {
  jobs: BoardJobProgress[]
  estimateSeconds: number
  generating: boolean
  error: string | null
  providerNotConfigured: boolean
  start: (params: BoardWideStartParams) => Promise<void>
  cancel: () => void
  reset: () => void
}

export interface BoardWideStartParams {
  caseId: string
  runId: string
  body: BoardWideGenerateRequest
}

// =============================================================================
// Helpers (module-level — not counted against hook line budget)
// =============================================================================

function isProviderNotConfigured(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  const body = err.body as { error?: string } | null
  return err.status === HTTP_BAD_REQUEST && body?.error === 'provider_not_configured'
}

function isTerminal(status: string): boolean {
  return status === 'succeeded' || status === 'failed'
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

interface PollOneJobParams {
  entry: BoardJobProgress
  caseId: string
  runId: string
  cancelledRef: React.MutableRefObject<boolean>
  onUpdated: (job: BriefingJob) => void
}

async function pollOneJob({ entry, caseId, runId, cancelledRef, onUpdated }: PollOneJobParams): Promise<void> {
  if (cancelledRef.current) return
  try {
    const updated = await pollBriefingJob({ caseId, runId, jobId: entry.job.id })
    onUpdated(updated)
  } catch {
    /* individual poll error — leave job in current state */
  }
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Board-wide briefing generation with per-cluster progress and cancel support.
 *
 * @behaviour start() POSTs the board-wide endpoint, stores the returned jobs, then polls
 *   each non-terminal job every POLL_INTERVAL_MS. cancel() marks pending jobs client-side
 *   as cancelled and stops polling. Cleans up on unmount.
 * @errors 400 provider_not_configured → providerNotConfigured:true; others → error string.
 */
export function useBoardWideGenerate(): UseBoardWideGenerateResult {
  const [jobs, setJobs] = useState<BoardJobProgress[]>([])
  const [estimateSeconds, setEstimateSeconds] = useState(0)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [providerNotConfigured, setProviderNotConfigured] = useState(false)

  const cancelledRef = useRef(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef(true)
  const jobsRef = useRef<BoardJobProgress[]>([])
  const sessionRef = useRef<{ caseId: string; runId: string } | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false; clearActiveInterval() }
  }, [])

  function clearActiveInterval(): void {
    if (intervalRef.current !== null) { clearInterval(intervalRef.current); intervalRef.current = null }
  }

  function updateJobInState(updatedJob: BriefingJob): void {
    const next = jobsRef.current.map((e) => e.job.id === updatedJob.id ? { ...e, job: updatedJob } : e)
    jobsRef.current = next
    if (mountedRef.current) setJobs([...next])
  }

  const pollActiveJobs = useCallback(async () => {
    if (cancelledRef.current || sessionRef.current === null) return
    const { caseId, runId } = sessionRef.current
    const pending = jobsRef.current.filter((e) => !e.cancelled && !isTerminal(e.job.status))
    await Promise.allSettled(
      pending.map((entry) => pollOneJob({ entry, caseId, runId, cancelledRef, onUpdated: updateJobInState }))
    )
    const allDone = jobsRef.current.every((e) => e.cancelled || isTerminal(e.job.status))
    if (allDone && mountedRef.current) { clearActiveInterval(); setGenerating(false) }
  }, [])

  const start = useCallback(async (params: BoardWideStartParams) => {
    const { caseId, runId, body } = params
    cancelledRef.current = false
    sessionRef.current = { caseId, runId }
    setError(null); setProviderNotConfigured(false); setJobs([]); setEstimateSeconds(0); setGenerating(true)
    try {
      const response = await boardWideGenerate({ caseId, runId, body })
      if (!mountedRef.current) return
      const initialJobs: BoardJobProgress[] = response.jobs.map((job) => ({ job, cancelled: false }))
      jobsRef.current = initialJobs
      setJobs(initialJobs)
      setEstimateSeconds(response.estimateSeconds)
      if (initialJobs.every((e) => isTerminal(e.job.status))) { setGenerating(false); return }
      clearActiveInterval()
      intervalRef.current = setInterval(() => { void pollActiveJobs() }, POLL_INTERVAL_MS)
    } catch (err) {
      if (!mountedRef.current) return
      setGenerating(false)
      if (isProviderNotConfigured(err)) { setProviderNotConfigured(true) }
      else { setError(messageOf(err, 'Failed to start board-wide generation')) }
    }
  }, [pollActiveJobs])

  const cancel = useCallback(() => {
    cancelledRef.current = true
    clearActiveInterval()
    setGenerating(false)
    const cancelled = jobsRef.current.map((e) => isTerminal(e.job.status) ? e : { ...e, cancelled: true })
    jobsRef.current = cancelled
    setJobs([...cancelled])
  }, [])

  const reset = useCallback(() => {
    clearActiveInterval()
    cancelledRef.current = false
    sessionRef.current = null
    jobsRef.current = []
    setJobs([]); setEstimateSeconds(0); setGenerating(false); setError(null); setProviderNotConfigured(false)
  }, [])

  return { jobs, estimateSeconds, generating, error, providerNotConfigured, start, cancel, reset }
}
